import React from 'react'
import { withTableConfigs } from '../../components/table'
import { I18n } from 'react-redux-i18n'
import CheckInSceneLinkButton from './CheckInSceneLinkButton'
import ServiceTxnTableFilterPanel from './ServiceTxnTableFilterPanel'
import { ServiceTxnCancelConfirmModal } from '../../components/ServiceTxnCancelConfirmModal'
import CheckInInfo from '../../components/serviceTxn/CheckInInfo'

class ServiceTxnTable extends React.Component {
  state = {
    selectedRows: [],
  }

  handleSelectAll = (e) => {
    const { defaultSettings } = this.props
    const data = defaultSettings.data || []

    if (e.target.checked) {
      this.setState({ selectedRows: data.map((row) => row._id) })
    } else {
      this.setState({ selectedRows: [] })
    }
  }

  handleSelectRow = (rowId) => {
    this.setState((prevState) => {
      const { selectedRows } = prevState
      if (selectedRows.includes(rowId)) {
        return { selectedRows: selectedRows.filter((id) => id !== rowId) }
      } else {
        return { selectedRows: [...selectedRows, rowId] }
      }
    })
  }

  isRowSelected = (rowId) => {
    return this.state.selectedRows.includes(rowId)
  }

  isAllSelected = () => {
    const { defaultSettings } = this.props
    const data = defaultSettings.data || []
    return data.length > 0 && this.state.selectedRows.length === data.length
  }

  handleCheckoutSelected = () => {
    const { defaultSettings, onMultiCheckout } = this.props
    const data = defaultSettings.data || []
    const selectedData = data.filter((row) =>
      this.state.selectedRows.includes(row._id),
    )

    // Call multi-checkout handler if provided
    if (onMultiCheckout) {
      onMultiCheckout(selectedData)
    }

    // Clear selection after checkout
    this.setState({ selectedRows: [] })
  }

  handleRefreshSelected = () => {
    // Clear all selections
    this.setState({ selectedRows: [] })
  }

  render() {
    const {
      defaultOptions,
      defaultSettings,
      onCheckoutClick,
      onFeedbackClick,
      onExtendDurationClick,
      renderFilterPanel,
      isCancelable,
      serviceTxnDidConfirm,
      renderToolbar,
      actionable = true,
      showCheckoutButton = true,
      showMultiSelect = true,
    } = this.props

    const data = defaultSettings.data || []
    const { page, sizePerPage, sizePerPageList, onPageChange } = defaultOptions
    const totalCount = defaultSettings.fetchInfo?.dataTotalSize || 0

    const totalPages = Math.ceil(totalCount / sizePerPage)
    const startIndex = (page - 1) * sizePerPage + 1
    const endIndex = Math.min(page * sizePerPage, totalCount)
    const hasSelection = this.state.selectedRows.length > 0

    return (
      <div>
        {renderFilterPanel}

        {/* Toolbar and Multi-select Actions */}
        <div className="flex items-center justify-between mb-3">
          {/* Multi-select Action Buttons - Only show if multiselect is enabled */}
          {showMultiSelect ? (
            <div className="flex items-center gap-2">
              <button
                onClick={this.handleRefreshSelected}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors shadow-sm flex items-center gap-2 font-medium text-sm"
              >
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                    clipRule="evenodd"
                  />
                </svg>
                Refresh
              </button>
              {showCheckoutButton && hasSelection && (
                <button
                  onClick={this.handleCheckoutSelected}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm flex items-center gap-2 font-medium text-sm"
                >
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Checkout ({this.state.selectedRows.length})
                </button>
              )}
            </div>
          ) : (
            <div></div>
          )}

          {/* Filter Toolbar (right side) */}
          {renderToolbar}
        </div>

        {/* Table Container */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Table Wrapper with Scroll */}
          <div
            className="overflow-x-auto"
            style={{ maxHeight: '44vh', overflowY: 'auto' }}
          >
            <table className="w-full">
              <thead className="bg-gray-100 sticky top-0 z-10">
                <tr className="border-b border-gray-200">
                  {/* Checkbox Column - Only show if multiselect is enabled */}
                  {showMultiSelect && (
                    <th className="px-4 py-3 text-left w-12">
                      <input
                        type="checkbox"
                        checked={this.isAllSelected()}
                        onChange={this.handleSelectAll}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Passenger Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Check-In Info
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-28">
                    Seat No.
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-28">
                    Start Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-28">
                    End Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-28">
                    Out Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-28">
                    Over Time
                  </th>
                  {actionable && (
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider w-44">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={(showMultiSelect ? 1 : 0) + (actionable ? 8 : 7)}
                      className="px-4 py-12 text-center text-gray-500"
                    >
                      No data available
                    </td>
                  </tr>
                ) : (
                  data.map((serviceTxn) => {
                    const rowBgClass = getRowBackgroundClass(serviceTxn)
                    const isSelected = this.isRowSelected(serviceTxn._id)

                    return (
                      <tr
                        key={serviceTxn._id}
                        className={`hover:bg-gray-50 transition-colors ${rowBgClass} ${
                          isSelected && showMultiSelect ? 'bg-primary-50' : ''
                        }`}
                      >
                        {/* Checkbox - Only show if multiselect is enabled */}
                        {showMultiSelect && (
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() =>
                                this.handleSelectRow(serviceTxn._id)
                              }
                              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            />
                          </td>
                        )}
                        {/* Passenger Name */}
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {renderCustomerFullName(
                            serviceTxn.customerName,
                            serviceTxn,
                          )}
                        </td>
                        {/* Check-In Info */}
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {renderCheckInInfo(
                            serviceTxn.checkInType,
                            serviceTxn,
                          )}
                        </td>
                        {/* Seat No. */}
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {serviceTxn.seatNumber || '-'}
                        </td>
                        {/* Start Time */}
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {renderTime(serviceTxn.serviceInfo?.inTime)}
                        </td>
                        {/* End Time */}
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {renderTime(serviceTxn.serviceInfo?.issuedOutTime)}
                        </td>
                        {/* Out Time */}
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {renderTime(serviceTxn.serviceInfo?.outTime)}
                        </td>
                        {/* Over Time */}
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {serviceTxn.serviceInfo?.overTime
                            ? serviceTxn.serviceInfo.overTime + ' hr.'
                            : '-'}
                        </td>
                        {/* Actions */}
                        {actionable && (
                          <td className="px-4 py-3">
                            {createRenderTableActions({
                              onCheckoutClick,
                              onFeedbackClick,
                              onExtendDurationClick,
                              isCancelable,
                              serviceTxnDidConfirm,
                            })(null, serviceTxn)}
                          </td>
                        )}
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination - Only show when there's data */}
          {totalCount > 0 && (
            <div className="bg-gray-100 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              {/* Left: Page size selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Show</span>
                <select
                  value={sizePerPage}
                  onChange={(e) => onPageChange(1, parseInt(e.target.value))}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {sizePerPageList.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.text}
                    </option>
                  ))}
                </select>
                <span className="text-sm text-gray-700">entries</span>
              </div>

              {/* Center: Info */}
              <div className="text-sm text-gray-700">
                Showing {startIndex} to {endIndex} of {totalCount} entries
              </div>

              {/* Right: Page navigation */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => onPageChange(page - 1, sizePerPage)}
                  disabled={page === 1}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>

                {/* Page numbers */}
                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum
                    if (totalPages <= 5) {
                      pageNum = i + 1
                    } else if (page <= 3) {
                      pageNum = i + 1
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i
                    } else {
                      pageNum = page - 2 + i
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => onPageChange(pageNum, sizePerPage)}
                        className={`px-3 py-1.5 border rounded-lg text-sm transition-colors ${
                          page === pageNum
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}
                </div>

                <button
                  onClick={() => onPageChange(page + 1, sizePerPage)}
                  disabled={page === totalPages}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
}

const renderCustomerFullName = (customerName, serviceTxn) => (
  <CheckInSceneLinkButton serviceTxn={serviceTxn}>
    {customerName || '-'}
  </CheckInSceneLinkButton>
)

const renderCheckInInfo = (checkInType, serviceTxn) => {
  return <CheckInInfo serviceTxn={serviceTxn} />
}

const renderTime = (dateTime) => {
  const timeFormat = I18n.t('date.timeFormat')
  return dateTime ? dateTime.format(timeFormat) : '-'
}

const getRowBackgroundClass = (serviceTxn) => {
  if (serviceTxn.stateCode === 2) return 'bg-yellow-50'
  else if (serviceTxn.stateCode === 1) return 'bg-red-50'
  else return ''
}

const createRenderTableActions =
  ({
    onCheckoutClick,
    onFeedbackClick,
    onExtendDurationClick,
    isCancelable,
    serviceTxnDidConfirm,
  }) =>
  (_, serviceTxn) => (
    <ServiceTxnCancelConfirmModal
      serviceTxn={serviceTxn}
      serviceTxnDidConfirm={serviceTxnDidConfirm}
    >
      {(showModal) => (
        <div className="flex gap-2 justify-center">
          {/* Checkout Button */}
          <button
            onClick={() => onCheckoutClick(serviceTxn)}
            className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm"
            title="Check-out"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {/* Cancel Button */}
          {isCancelable(serviceTxn) && (
            <button
              onClick={showModal}
              className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm"
              title="Cancel"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}

          {/* Feedback Button */}
          <button
            onClick={() => onFeedbackClick(serviceTxn)}
            className="p-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors shadow-sm"
            title="Add Customer Feedback"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {/* Extend Duration Button */}
          {serviceTxn.serviceInfo.overTime > 0 && (
            <button
              onClick={() => onExtendDurationClick(serviceTxn)}
              className="p-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors shadow-sm"
              title="Extend Time Limit"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
        </div>
      )}
    </ServiceTxnCancelConfirmModal>
  )

export default withTableConfigs(ServiceTxnTable, ServiceTxnTableFilterPanel)
